import { findQuotations } from "@/lib/tool-kit/text-analysis/quotations";
import { findReferenceList } from "@/lib/tool-kit/text-analysis/sections";
import {
  countWords,
  type TextSpan,
} from "@/lib/tool-kit/text-analysis/segment";

/**
 * The word count an essay limit usually means — the body, without the parts most
 * institutions leave out.
 *
 * Each exclusion is found separately and then masked out of the text in a fixed order,
 * so every excluded word is attributed to exactly one rule and the per-rule figures add
 * up to the difference between the two totals. Only the body is searched for headings,
 * quotations and citations: the reference list is either counted whole or excluded
 * whole, never stripped of its years.
 */

export type ExclusionKind =
  | "references"
  | "headings"
  | "quotations"
  | "citations"
  | "numeric";

export const EXCLUSION_KINDS: readonly ExclusionKind[] = [
  "references",
  "headings",
  "quotations",
  "citations",
  "numeric",
];

export type AcademicCountOptions = Record<ExclusionKind, boolean>;

export interface Exclusion extends TextSpan {
  kind: ExclusionKind;
}

export interface KindTotal {
  /** How many were found, excluded or not. */
  items: number;
  /** Words removed by this rule (0 when the rule is off). */
  words: number;
}

export interface AcademicCount {
  total: number;
  counted: number;
  kinds: Record<ExclusionKind, KindTotal>;
  /** Every span found, tagged by rule — for highlighting and listing. */
  found: Exclusion[];
  /** The reference list heading as written, when one was found. */
  referenceHeading: string | null;
}

/* ------------------------------------------------------------------- headings */

const LINE_RE = /[^\n]+/g;
const HEADING_START_RE =
  /^(?:(?:\d{1,2}(?:\.\d{1,2})*\.?|[IVX]{1,4}\.)\s+)?["“‘']?\p{Lu}/u;
const HEADING_END_RE = /[.,;:]["'”’)]?$/;
const BULLET_RE = /^[-*•–]\s/;
const HEADING_MAX_WORDS = 12;
/** Three or more short lines in a row are a list or a title page, not headings. */
const LIST_RUN = 3;

function isHeadingLike(line: string): boolean {
  const words = countWords(line);
  return (
    words > 0 &&
    words <= HEADING_MAX_WORDS &&
    HEADING_START_RE.test(line) &&
    !HEADING_END_RE.test(line) &&
    !BULLET_RE.test(line)
  );
}

interface Line extends TextSpan {
  heading: boolean;
  /** A blank line comes before this one. */
  afterGap: boolean;
}

function nonEmptyLines(text: string, limit: number): Line[] {
  const lines: Line[] = [];
  let gap = true;
  for (const match of text.slice(0, limit).matchAll(LINE_RE)) {
    const raw = match[0];
    const content = raw.trim();
    if (content === "") {
      gap = true;
      continue;
    }
    const start = match.index + raw.indexOf(content);
    const previous = lines.at(-1);
    const blankBetween =
      previous !== undefined &&
      text.slice(previous.end, start).split("\n").length > 2;
    lines.push({
      start,
      end: start + content.length,
      heading: isHeadingLike(content),
      afterGap: gap || blankBetween,
    });
    gap = false;
  }
  return lines;
}

/**
 * Heading lines: short, capitalised, unpunctuated, and followed by more text. Lines
 * that touch each other form a run; a run of three or more is a list or a title page.
 */
export function findHeadings(text: string, limit = text.length): TextSpan[] {
  const lines = nonEmptyLines(text, limit);
  const headings: TextSpan[] = [];
  let i = 0;
  while (i < lines.length) {
    if (!lines[i].heading) {
      i++;
      continue;
    }
    let j = i + 1;
    while (j < lines.length && lines[j].heading && !lines[j].afterGap) {
      j++;
    }
    if (j - i < LIST_RUN && j < lines.length) {
      for (const line of lines.slice(i, j)) {
        headings.push({ start: line.start, end: line.end });
      }
    }
    i = j;
  }
  return headings;
}

/* ------------------------------------------------------------------ citations */

const PARENTHETICAL_RE = /\(([^()]{1,300})\)/g;
const PART_SPLIT_RE = /\s*;\s*/;
const LEAD_IN_RE =
  /^(?:e\.g\.,?|i\.e\.,?|see also|see|cf\.|as cited in|qtd\. in|quoted in|for (?:a )?(?:review|reviews|example|discussion),? see)\s+/i;
const YEAR_RE =
  /(?<!\d)(?:1[5-9]\d{2}|20\d{2})[a-rt-z]?(?!\d)|\bn\.d\.|\bin press\b|\bforthcoming\b/iu;
const EXTRA_YEAR_RE = /^,\s*(?:(?:1[5-9]\d{2}|20\d{2})[a-rt-z]?|n\.d\.)(?!\d)/i;
const AS_CITED_RE = /^,\s*as cited in\s+/i;
const LOCATOR_RE =
  /^[,:]?\s*(?:(?:p|pp|para|paras|ch|chap|chapter|sec|section|vol|fig|figure|table|slide|line|lines|n|nn|loc|col)\.?\s*)?[\divxlc:.]+(?:\s*[–-]\s*[\divxlc:.]+)?(?:\s*,\s*[\divxlc:.]+(?:\s*[–-]\s*[\divxlc:.]+)?)*(?:,\s*[\p{L}\s]{1,30})?$/iu;
const PERSONAL_COMMUNICATION_RE = /\bpersonal communication,/i;
const NOTE_TAIL_RE =
  /^,\s*(?:emphasis added|emphasis in original|italics added)$/i;
const TRAILING_COMMA_RE = /,\s*$/;
const AUTHOR_CHARS_RE = /^[\p{L}\p{M}'’.&,\s[\]-]+$/u;
const UPPER_START_RE = /^\p{Lu}/u;
const PAGE_TAIL_RE =
  /^(.+?),?\s+((?:\d+(?:\.\d+)*|[ivxlc]+)(?:\s*[–-]\s*(?:\d+(?:\.\d+)*|[ivxlc]+))?(?:\s*,\s*\d+(?:\s*[–-]\s*\d+)?)*)$/u;
const PAGE_ONLY_RE = /^(?:p|pp|para|paras)\.\s*\d+(?:\s*[–-]\s*\d+)?$/;
const QUOTE_THEN_PAGE_RE = /[”"’']\s*(\(\d{1,4}(?:\s*[–-]\s*\d{1,4})?\))/g;
const FIRST_WORD_RE = /^[\p{L}.]+/u;

/** Surname particles that may open a citation in lowercase: (de Souza, 2020). */
const PARTICLES = new Set([
  "de",
  "del",
  "della",
  "di",
  "da",
  "das",
  "dos",
  "du",
  "la",
  "le",
  "van",
  "von",
  "ten",
  "ter",
  "bin",
  "al",
  "el",
]);

function wordSet(...groups: string[]): ReadonlySet<string> {
  return new Set(groups.join(" ").split(" "));
}

/**
 * Capitalised words that open a parenthetical with a year in it but are not authors —
 * "(Fall 2020)", "(January 2021)", "(Census 2010)".
 */
const DATE_WORDS = wordSet(
  "january february march april may june july august september october november december",
  "jan feb mar apr jun jul aug sep sept oct nov dec spring summer fall autumn winter",
  "since until before after during from in circa census cohort wave class year years"
);

/**
 * Labels that open a parenthetical with a number in it — "(Table 2)", "(Study 3)",
 * "(Participant 12)". Without them, every labelled figure in a methods section would be
 * counted as an MLA author–page citation. Not applied to author–date citations, where a
 * year follows: "(Case & Deaton, 2020)" is a citation.
 */
const LABEL_WORDS = wordSet(
  "table tables figure figures fig chapter chapters ch section sections sec appendix",
  "study studies experiment experiments phase model models hypothesis hypotheses equation",
  "eq step steps level levels time item items question questions part session grade",
  "trial trials group groups condition conditions sample week weeks day days line lines",
  "page pages volume vol note notes box panel stage round unit module lesson task block",
  "scale factor version article rule room site school participant participants age ages",
  "patient patients case cases interview respondent subject exhibit plate map chart graph",
  "slide episode season act scene verse book issue no number type types category total"
);

function firstWord(text: string): string {
  return (FIRST_WORD_RE.exec(text)?.[0] ?? "").replace(".", "").toLowerCase();
}

function isAuthorish(
  text: string,
  blocked: ReadonlySet<string>,
  alsoBlocked: ReadonlySet<string> = blocked
): boolean {
  const name = text.replace(TRAILING_COMMA_RE, "").trim();
  if (name === "" || !AUTHOR_CHARS_RE.test(name) || countWords(name) > 12) {
    return false;
  }
  const first = firstWord(name);
  if (blocked.has(first) || alsoBlocked.has(first)) {
    return false;
  }
  return UPPER_START_RE.test(name) || PARTICLES.has(first);
}

/** What may follow the year: more years, a locator, or "as cited in" another source. */
function isCitationTail(rest: string): boolean {
  let tail = rest.trim();
  let extra = EXTRA_YEAR_RE.exec(tail);
  while (extra) {
    tail = tail.slice(extra[0].length).trim();
    extra = EXTRA_YEAR_RE.exec(tail);
  }
  if (tail === "") {
    return true;
  }
  if (AS_CITED_RE.test(tail)) {
    return isAuthorDatePart(tail.replace(AS_CITED_RE, ""));
  }
  return LOCATOR_RE.test(tail) || NOTE_TAIL_RE.test(tail);
}

/** "Smith, 2020", "Smith et al., 2020, p. 5", "World Health Organization [WHO], 2020", "2020". */
function isAuthorDatePart(part: string): boolean {
  const text = part.replace(LEAD_IN_RE, "");
  const year = YEAR_RE.exec(text);
  if (!year) {
    return false;
  }
  const before = text.slice(0, year.index).trim();
  if (PERSONAL_COMMUNICATION_RE.test(before)) {
    return true;
  }
  if (before !== "" && !isAuthorish(before, DATE_WORDS)) {
    return false;
  }
  return isCitationTail(text.slice(year.index + year[0].length));
}

/** MLA: "Smith 45", "Smith and Jones 45–46", "Shakespeare, Hamlet 3.1.56". */
function isAuthorPagePart(part: string): boolean {
  const text = part.replace(LEAD_IN_RE, "");
  const match = PAGE_TAIL_RE.exec(text);
  return match !== null && isAuthorish(match[1], LABEL_WORDS, DATE_WORDS);
}

function isCitationPart(part: string): boolean {
  return (
    isAuthorDatePart(part) || isAuthorPagePart(part) || PAGE_ONLY_RE.test(part)
  );
}

/** Parenthetical in-text citations in `[0, limit)`. */
export function findCitations(text: string, limit = text.length): TextSpan[] {
  const body = text.slice(0, limit);
  const spans: TextSpan[] = [];
  for (const match of body.matchAll(PARENTHETICAL_RE)) {
    const parts = match[1].trim().split(PART_SPLIT_RE);
    if (parts.every(isCitationPart)) {
      spans.push({ start: match.index, end: match.index + match[0].length });
    }
  }
  // MLA after a quotation: "…the green light" (180).
  for (const match of body.matchAll(QUOTE_THEN_PAGE_RE)) {
    const start = match.index + match[0].length - match[1].length;
    if (!spans.some((span) => span.start === start)) {
      spans.push({ start, end: start + match[1].length });
    }
  }
  return spans.sort((a, b) => a.start - b.start);
}

/* ------------------------------------------------------------ numeric markers */

const NUMERIC_CITATION_RE =
  /\[\s*\d{1,3}(?:\s*[,–-]\s*\d{1,3})*(?:\s*,\s*pp?\.\s*\d+(?:\s*[–-]\s*\d+)?)?\s*\]/g;
const SUPERSCRIPT_RE = /[⁰¹²³⁴⁵⁶⁷⁸⁹]+(?:[,⁻–-][⁰¹²³⁴⁵⁶⁷⁸⁹]+)*/g;

/**
 * Bracketed numeric citations ([1], [2, 5], [3–6]) and superscript digits typed as
 * Unicode (¹²). Ordinary digits that lost their superscript formatting in the paste
 * ("…was significant.12") are left alone: they cannot be told apart from numbers that
 * belong to the sentence.
 */
export function findNumericMarkers(
  text: string,
  limit = text.length
): TextSpan[] {
  const body = text.slice(0, limit);
  const spans: TextSpan[] = [];
  for (const re of [NUMERIC_CITATION_RE, SUPERSCRIPT_RE]) {
    for (const match of body.matchAll(re)) {
      spans.push({ start: match.index, end: match.index + match[0].length });
    }
  }
  return spans.sort((a, b) => a.start - b.start);
}

/* ---------------------------------------------------------------------- count */

function findAll(
  text: string,
  limit: number
): Record<ExclusionKind, TextSpan[]> {
  return {
    references: limit < text.length ? [{ start: limit, end: text.length }] : [],
    headings: findHeadings(text, limit),
    quotations: findQuotations(text, { blockMarker: true, limit }).quotations,
    citations: findCitations(text, limit),
    numeric: findNumericMarkers(text, limit),
  };
}

export function academicWordCount(
  text: string,
  options: AcademicCountOptions
): AcademicCount {
  const reference = findReferenceList(text);
  const limit = reference?.start ?? text.length;
  const spans = findAll(text, limit);
  const total = countWords(text);
  const mask = text.split("");
  const found: Exclusion[] = [];
  let current = total;
  const kinds = {} as Record<ExclusionKind, KindTotal>;
  for (const kind of EXCLUSION_KINDS) {
    kinds[kind] = { items: spans[kind].length, words: 0 };
    found.push(
      ...spans[kind].map((span) => ({ start: span.start, end: span.end, kind }))
    );
    if (!options[kind] || spans[kind].length === 0) {
      continue;
    }
    for (const span of spans[kind]) {
      mask.fill(" ", span.start, span.end);
    }
    const remaining = countWords(mask.join(""));
    kinds[kind].words = current - remaining;
    current = remaining;
  }
  return {
    total,
    counted: current,
    kinds,
    found,
    referenceHeading: reference?.heading ?? null,
  };
}
